import { useState } from "react";
import { Star, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiError, http } from "@/lib/api";

export const Stars = ({ value, size = "h-4 w-4", testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid} aria-label={`${value} de 5 estrelas`}>
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-[var(--pink)] text-[var(--pink)]" : "text-[var(--line)]"}`} />)}
  </span>
);

function StarInput({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="review-rating-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => onChange(n)} aria-label={`${n} estrelas`} className="transition-transform hover:scale-110">
          <Star className={`h-8 w-8 ${n <= value ? "fill-[var(--pink)] text-[var(--pink)]" : "text-[var(--line)]"}`} />
        </button>
      ))}
    </div>
  );
}

export function ReviewDialog({ product, preset, onClose, onDone }) {
  const [f, setF] = useState({ number: preset?.number || "", phone: preset?.phone || "", name: "", rating: 0, text: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error("Escolhe de 1 a 5 estrelas");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("product_id", product.id);
    files.forEach((x) => fd.append("files", x));
    try {
      await http.post("/reviews", fd);
      toast.success("Obrigado! A tua avaliação aparece depois de aprovada pela DUSA.");
      onDone?.(); onClose();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-[var(--paper)]" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Avaliar {product.name}</DialogTitle><DialogDescription className="sr-only">Formulário de avaliação do produto</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!preset && <p className="text-xs text-[var(--muted-ink)]">Só quem comprou pode avaliar. Indica o número do pedido (está no email de confirmação) e o telefone usado na compra.</p>}
          {!preset && (
            <div className="grid gap-3 sm:grid-cols-2">
              <input required data-testid="review-order-number" className="field" placeholder="Nº do pedido (ex.: D1001)" value={f.number} onChange={set("number")} />
              <input required data-testid="review-phone" className="field" placeholder="Telefone" value={f.phone} onChange={set("phone")} />
            </div>
          )}
          <input required minLength={2} maxLength={60} data-testid="review-name" className="field" placeholder="O teu nome (aparece na avaliação)" value={f.name} onChange={set("name")} />
          <StarInput value={f.rating} onChange={(rating) => setF({ ...f, rating })} />
          <textarea required minLength={5} maxLength={1000} data-testid="review-text" className="field min-h-28" placeholder="Conta-nos o que achaste…" value={f.text} onChange={set("text")} />
          <div className="flex flex-wrap gap-2">
            {files.map((x, i) => (
              <div key={`${x.name}-${x.size}`} className="relative h-16 w-16 overflow-hidden rounded-xl">
                <img src={URL.createObjectURL(x)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5" aria-label="Remover foto"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {files.length < 3 && (
              <label className="flex h-16 cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-[var(--line)] px-4 text-xs hover:border-[var(--violet)]">
                <Upload className="h-4 w-4 text-[var(--violet)]" />Fotos (até 3)
                <input data-testid="review-photos-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)].slice(0, 3)); e.target.value = ""; }} />
              </label>
            )}
          </div>
          <button data-testid="review-submit" disabled={busy} className="btn-primary w-full">{busy ? "A enviar…" : "Enviar avaliação"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
